import React, { FC, ChangeEvent } from 'react'

import { Box, FormControlLabel, Checkbox, Button, makeStyles } from '@material-ui/core'
import { useParams } from 'react-router'

import { useGetPatientInfo, useGetPracticeInfo, useSignoffInterval } from 'store'
import { SnackbarError } from 'components'
import { format } from 'utils/date'


const useSignoffStyles = makeStyles((theme) => ({
	button: {
		background: theme.palette.alertGreen
	}
}))

const SignoffView: FC = () => {
	const styles = useSignoffStyles()

	const { patientId, intervalId } = useParams<{ patientId: string, intervalId: string }>()
	const { patient } = useGetPatientInfo(patientId)
	const [doSignoff, signoffStatus] = useSignoffInterval(intervalId)
	const practiceInfo = useGetPracticeInfo()

	let selectedInterval = patient?.intervals.find(i => i.id === intervalId) || patient?.intervals[0]

	let isNonbillable = selectedInterval?.signoff.find(a => !!a.nonbillable)
	let is99470 = selectedInterval?.signoff.find(a => !!a.cpt_99470)
	let is99457 = selectedInterval?.signoff.find(a => !!a.cpt_99457)
	let is99458 = selectedInterval?.signoff.find(a => !!a.cpt_99458)
	let is99458b = selectedInterval?.signoff.find(a => !!a.cpt_99458_2)
	let is99445 = selectedInterval?.signoff.find(a => !!a.cpt_99445)
	let is99454 = selectedInterval?.signoff.find(a => !!a.cpt_99454)

	let approval = selectedInterval?.signoff.find(a => !!a.approvedOn)

	const onCheckOption99470 = (evt: ChangeEvent<HTMLInputElement>) => {
		doSignoff({ cpt_99470: evt.target.checked })
	}
	const onCheckOption99457 = (evt: ChangeEvent<HTMLInputElement>) => {
		doSignoff({ cpt_99457: evt.target.checked })
	}
	const onCheckOption99458 = (evt: ChangeEvent<HTMLInputElement>) => {
		doSignoff({ cpt_99458: evt.target.checked })
	}
	const onCheckOption99458b = (evt: ChangeEvent<HTMLInputElement>) => {
		doSignoff({ cpt_99458_2: evt.target.checked })
	}
	
	const onCheckOption99445 = (evt: ChangeEvent<HTMLInputElement>) => {
		doSignoff({ cpt_99445: evt.target.checked })
	}
	const onCheckOption99454 = (evt: ChangeEvent<HTMLInputElement>) => {
		doSignoff({ cpt_99454: evt.target.checked })
	}

	const onCheckNonbillable = (evt: ChangeEvent<HTMLInputElement>) => {
		doSignoff({ nonbillable: evt.target.checked })
	}

	const onApprove = () => {
		doSignoff({ approved: true })
	}

	// RPM-95 changes how approval is admissable
	// For 99454: data readings (daysOfData) is required
	// For 99457: data readings are not required
	const canApprove = (((!!is99445 || !!is99454) && !!selectedInterval?.daysOfData) || !!is99470 || !!is99457 || !!isNonbillable) && !!selectedInterval?.internalNote && selectedInterval
	
	const customIntervals = !!practiceInfo.practice?.customInterval
	return (
		<Box mx={2} mt={2} mb={1} display="flex" className="printHidden">
			<SnackbarError message="Failed to update signoff" error={signoffStatus.error} />
			<Box flex={1}>
				<Box style={{height:customIntervals ? "3rem" : "auto"}}>
					<FormControlLabel
						control={<Checkbox disabled={!!approval || signoffStatus.loading || !!is99457 || !!isNonbillable} size="small" checked={!!is99470} onChange={onCheckOption99470} name="99470" />}
						label="I acknowledge that I have read for at least 10 minutes but no more than 19 minutes."
					/>
				</Box>
				<Box style={{height:customIntervals ? "3rem" : "auto", whiteSpace: "nowrap"}}>
					<FormControlLabel
						control={<Checkbox disabled={!!approval || signoffStatus.loading || !!is99470 || !!is99458 || !!isNonbillable} size="small" checked={!!is99457} onChange={onCheckOption99457} name="99457" />}
						label="I acknowledge that I have read for at least 20 minutes."
					/>
					<FormControlLabel
						control={<Checkbox  disabled={!!approval || !is99457 || signoffStatus.loading || !!is99458b} size="small" checked={!!is99458} onChange={onCheckOption99458} name="99458" />}
						label="Additional 20 minutes."
					/>
					<FormControlLabel
						control={<Checkbox  disabled={!!approval || !is99458 || signoffStatus.loading} size="small" checked={!!is99458b} onChange={onCheckOption99458b} name="99458_2" />}
						label="Second additional 20 minutes."
					/>
				</Box>
				{customIntervals && 
				<Box style={{height:"3rem", whiteSpace: "nowrap"}}>
					<FormControlLabel
						control={<Checkbox disabled={!!approval || !!is99454 || signoffStatus.loading || !!isNonbillable} size="small" checked={!!is99445} onChange={onCheckOption99445} name="99445" />}
						label={`Device supplied by clinic. Received at least 2-15 days of data.`}
					/>
					<FormControlLabel
						control={<Checkbox disabled={!!approval || !!is99445 || signoffStatus.loading || !!isNonbillable} size="small" checked={!!is99454} onChange={onCheckOption99454} name="99454" />}
						label={`Received at least 16 days of data. (Current count ${selectedInterval?.daysOfData})`}
					/>
				</Box>}
			</Box>
			<Box>
				{approval && `Approved by: ${approval.practitioner.name}`}
				{approval?.approvedOn &&  <Box>{format(new Date(approval.approvedOn || 0), "P p")}</Box>}
				{!approval && <Button className={styles.button} onClick={onApprove} disabled={!canApprove || signoffStatus.loading} color="secondary" variant="contained">Approve</Button>}
				<br/><br/>
				<Box style={{height:"3rem"}}>
					<FormControlLabel
						control={<Checkbox disabled={!!approval || signoffStatus.loading || !!is99457 || !!is99454} size="small" checked={!!isNonbillable} onChange={onCheckNonbillable} name="nonbillable" />}
						label="Non-Billable"
					/>
				</Box>
			</Box>
		</Box>
	)
}

export default SignoffView